import { InfoIcon } from '@chakra-ui/icons';
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Select, SingleValue } from 'chakra-react-select';
import React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import { DraftRepresentative, IdentityType, RepresentativeRole } from '@blockpulse3/graphql/hooks';
import { alpha3CountryOptions, formatCountry } from '@blockpulse3/helpers';
import { ErrorMessage } from '@blockpulse3/ui/commons';

import { CompanyOption, SearchCompanySelect } from '../../../../../SearchCompanySelect';
import {
  draftRepresentativeDefaultValues,
  spvLegalRepresentativePresidentSchema,
  spvLegalRepresentativeSchema,
} from '../../../provider/schema';
import { revocationMajorityOptions } from '../constants';

type Props = {
  /* ** Available roles for a representant form, must be Intl values ** */
  roles: (keyof IntlMessages['RoleValues'])[];
  /* ** Is role picker displayed ** */
  shouldDisplayRoles: boolean;
  /* ** Default form values ** */
  defaultValues?: DraftRepresentative;
  /* ** Is the Cancel button disabled ** */
  shouldCancel?: boolean;
  /* ** Callback of the Cancel button ** */
  onCancel?: () => void;
  /* ** Callback of the Submit button ** */
  onSubmit: (data: DraftRepresentative) => void;
};

/**
 * LegalRepresentantForm.
 * SPV.
 * Legal version of the RepresentantForm. Uses a forwardRef function to be able to submit it
 * from the outside.
 */
export const LegalRepresentantInformationsForm = React.forwardRef<HTMLFormElement, Props>(
  (props, ref) => {
    const {
      roles,
      shouldDisplayRoles,
      defaultValues = { ...draftRepresentativeDefaultValues, type: IdentityType.COMPANY },
      shouldCancel = false,
      onCancel = noop,
      onSubmit,
    } = props;

    const i18nGeneral = useTranslations('General');
    const i18nRoleValues = useTranslations('RoleValues');
    const i18nRepresentantInformationsForm = useTranslations(
      'SPVLegalRepresentantInformationsForm',
    );
    const i18nRevocationMajorityValues = useTranslations('RevocationMajorityValues');

    const { register, control, formState, setValue, trigger, handleSubmit } =
      useForm<DraftRepresentative>({
        defaultValues,
        resolver: yupResolver(
          roles.includes(RepresentativeRole.PRESIDENT)
            ? spvLegalRepresentativePresidentSchema
            : spvLegalRepresentativeSchema,
        ),
      });

    /* ** Update fields on company name change ** */
    const handleNameChange = (option: SingleValue<CompanyOption>): void => {
      if (!option) {
        return;
      }
      setValue('name', option.name);
      setValue('creationDate', option.creationDate);
      setValue('corporateForm', option.corporateForm);
      setValue('shareCapital', option.shareCapital);
      setValue('registrationNumber', option.registrationNumber);
      setValue('registrationEntity', option.registrationEntity);
      setValue('address.line', option.address.line);
      setValue('address.city', option.address.city);
      setValue('address.postalCode', option.address.postalCode);
      setValue('address.countryData', {
        label: formatCountry(option.address.country),
        value: option.address.country,
      });
      setValue('businessActivityDescription', option.businessActivityDescription);
      trigger();
    };

    const handleFormSubmit: SubmitHandler<DraftRepresentative> = (data): void => {
      onSubmit(data);
    };

    const handleFormCancel = (): void => {
      onCancel();
    };

    return (
      <form ref={ref} onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack spacing="8">
          <Stack spacing="4">
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              {shouldDisplayRoles && (
                <Controller
                  control={control}
                  name="role"
                  render={({ field }): JSX.Element => (
                    <FormControl isInvalid={!!formState.errors?.role} width="50%">
                      <FormLabel htmlFor="role">{i18nRoleValues('RoleField')}</FormLabel>
                      <RadioGroup id="role" {...field}>
                        <HStack spacing="4">
                          {roles.map((role) => (
                            <Radio key={role} value={role}>
                              {i18nRoleValues(role)}
                            </Radio>
                          ))}
                        </HStack>
                      </RadioGroup>
                      <ErrorMessage error={formState.errors?.role} />
                    </FormControl>
                  )}
                />
              )}
              {roles.includes(RepresentativeRole.PRESIDENT) && (
                <Controller
                  control={control}
                  name="revocationMajorityData"
                  render={({ field: statusField }): JSX.Element => (
                    <FormControl
                      isInvalid={!!formState.errors?.revocationMajorityData}
                      maxWidth={{ base: 'full', md: '50%' }}
                    >
                      <FormLabel htmlFor="revocationMajorityData">
                        {i18nRepresentantInformationsForm('RevocationMajorityField')}
                      </FormLabel>
                      <Select
                        id="revocationMajorityData"
                        isSearchable={false}
                        menuPlacement="auto"
                        options={revocationMajorityOptions}
                        getOptionLabel={(option): string =>
                          option.label
                            ? i18nRevocationMajorityValues(
                                option.label as keyof IntlMessages['RevocationMajorityValues'],
                              )
                            : ''
                        }
                        {...statusField}
                      />
                      <ErrorMessage error={formState.errors?.revocationMajorityData?.value} />
                    </FormControl>
                  )}
                />
              )}
            </Stack>
            <FormControl>
              <FormLabel htmlFor="searchCompany">
                {i18nRepresentantInformationsForm('SearchCompanyField')}
              </FormLabel>
              <SearchCompanySelect
                menuPlacement="auto"
                placeholder={i18nRepresentantInformationsForm('SearchCompanyPlaceholder')}
                value={null}
                onChange={handleNameChange}
              />
            </FormControl>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.name}>
                <FormLabel htmlFor="name">
                  {i18nRepresentantInformationsForm('NameField')}
                </FormLabel>
                <Input id="name" type="text" {...register('name')} />
                <ErrorMessage error={formState.errors?.name} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.creationDate}>
                <FormLabel htmlFor="creationDate">
                  {i18nRepresentantInformationsForm('CreationDateField')}
                </FormLabel>
                <Input id="creationDate" type="date" {...register('creationDate')} />
                <ErrorMessage error={formState.errors?.creationDate} />
              </FormControl>
            </Stack>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.corporateForm}>
                <HStack alignItems="flex-start" spacing="0">
                  <FormLabel htmlFor="corporateForm">
                    {i18nRepresentantInformationsForm('CorporateFormField')}
                  </FormLabel>
                </HStack>
                <Input id="corporateForm" type="text" {...register('corporateForm')} />
                <ErrorMessage error={formState.errors?.corporateForm} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.shareCapital}>
                <HStack alignItems="flex-start" spacing="0">
                  <FormLabel htmlFor="shareCapital">
                    {i18nRepresentantInformationsForm('ShareCapitalField')}
                  </FormLabel>
                </HStack>
                <InputGroup>
                  <Input
                    id="shareCapital"
                    step="0.01"
                    type="number"
                    {...register('shareCapital')}
                  />
                  <InputRightAddon>€</InputRightAddon>
                </InputGroup>
                <ErrorMessage error={formState.errors?.shareCapital} />
              </FormControl>
            </Stack>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.registrationNumber}>
                <HStack alignItems="flex-start" spacing="0">
                  <FormLabel htmlFor="registrationNumber">
                    {i18nRepresentantInformationsForm('IdentificationNumberField')}
                  </FormLabel>
                  <Tooltip
                    hasArrow
                    label={i18nRepresentantInformationsForm('Tooltip.IdentificationNumberField')}
                    placement="top"
                  >
                    <Icon as={InfoIcon} color="gray.500" />
                  </Tooltip>
                </HStack>
                <Input id="registrationNumber" type="text" {...register('registrationNumber')} />
                <ErrorMessage error={formState.errors?.registrationNumber} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.registrationEntity}>
                <HStack alignItems="flex-start" spacing="0">
                  <FormLabel htmlFor="registrationEntity">
                    {i18nRepresentantInformationsForm('IdentificationEntityField')}
                  </FormLabel>
                </HStack>
                <Input id="registrationEntity" type="text" {...register('registrationEntity')} />
                <ErrorMessage error={formState.errors?.registrationEntity} />
              </FormControl>
            </Stack>
            <Stack bg="gray.50" borderRadius="md" p="3" spacing="2">
              <Text fontSize="lg" fontWeight="bold">
                {i18nRepresentantInformationsForm('Address.Title')}
              </Text>
              <Stack spacing="4">
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.address?.line}>
                    <FormLabel htmlFor="line">
                      {i18nRepresentantInformationsForm('Address.LineField')}
                    </FormLabel>
                    <Input id="line" type="text" {...register('address.line')} />
                    <ErrorMessage error={formState.errors?.address?.line} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.address?.city}>
                    <FormLabel htmlFor="city">
                      {i18nRepresentantInformationsForm('Address.CityField')}
                    </FormLabel>
                    <Input id="city" type="text" {...register('address.city')} />
                    <ErrorMessage error={formState.errors?.address?.city} />
                  </FormControl>
                </Stack>
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors.address?.postalCode}>
                    <FormLabel htmlFor="zipcode">
                      {i18nRepresentantInformationsForm('Address.ZipCodeField')}
                    </FormLabel>
                    <Input id="zipcode" type="text" {...register('address.postalCode')} />
                    <ErrorMessage error={formState.errors?.address?.postalCode} />
                  </FormControl>
                  <Controller
                    control={control}
                    name="address.countryData"
                    render={({ field }): JSX.Element => (
                      <FormControl isInvalid={!!formState.errors.address?.countryData}>
                        <FormLabel htmlFor="country">
                          {i18nRepresentantInformationsForm('Address.CountryField')}
                        </FormLabel>
                        <Select
                          id="country"
                          menuPlacement="auto"
                          options={alpha3CountryOptions}
                          {...field}
                        />
                        <ErrorMessage error={formState.errors.address?.countryData?.value} />
                      </FormControl>
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>
            <Stack bg="gray.50" borderRadius="md" p="3" spacing="2">
              <Text fontSize="lg" fontWeight="bold">
                {i18nRepresentantInformationsForm('LastNaturalRepresentantTitle')}
              </Text>
              <Stack spacing="4">
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.firstName}>
                    <FormLabel htmlFor="firstName">
                      {i18nRepresentantInformationsForm('FirstNameField')}
                    </FormLabel>
                    <Input id="firstName" type="text" {...register('firstName')} />
                    <ErrorMessage error={formState.errors?.firstName} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.lastName}>
                    <FormLabel htmlFor="lastName">
                      {i18nRepresentantInformationsForm('LastNameField')}
                    </FormLabel>
                    <Input id="lastName" type="text" {...register('lastName')} />
                    <ErrorMessage error={formState.errors?.lastName} />
                  </FormControl>
                </Stack>
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.position}>
                    <FormLabel htmlFor="position">
                      {i18nRepresentantInformationsForm('RoleField')}
                    </FormLabel>
                    <Input id="position" type="text" {...register('position')} />
                    <ErrorMessage error={formState.errors?.position} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.email}>
                    <FormLabel htmlFor="email">
                      {i18nRepresentantInformationsForm('EmailField')}
                    </FormLabel>
                    <Input id="email" type="text" {...register('email')} />
                    <ErrorMessage error={formState.errors?.email} />
                  </FormControl>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <Button
              isDisabled={shouldCancel}
              type="button"
              variant="secondary"
              w="full"
              onClick={handleFormCancel}
            >
              {i18nGeneral('Delete')}
            </Button>
            <Button type="submit" w="full">
              {i18nGeneral('Submit')}
            </Button>
          </Stack>
        </Stack>
      </form>
    );
  },
);

LegalRepresentantInformationsForm.displayName = 'LegalRepresentantInformationsForm';

export type LegalRepresentantInformationsFormProps = Props;
